import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    businessType: "",
    adSpend: "",
    message: "",
  });
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            company: formData.businessType,
            message: `Ad Spend: ${formData.adSpend}\n\nChallenge: ${formData.message}`,
            source: 'free-audit'
          }
        ]);

      if (error) throw error;

      toast({
        title: "Audit request received!",
        description: "We'll review your details and get back to you within 24 hours.",
      });

      setFormData({
        name: "",
        email: "",
        businessType: "",
        adSpend: "",
        message: "",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-card border border-border p-8 rounded-2xl shadow-lg sticky top-24"
    >
      <h2 className="text-2xl font-bold mb-2 text-foreground">Request Your Free Audit</h2>
      <p className="text-muted-foreground mb-6">Fill in your details and we'll get back to you within 24 hours.</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-2 text-foreground">Name</label>
          <Input
            id="name"
            name="name"
            placeholder="Your name"
            value={formData.name}
            onChange={handleChange}
            required
            maxLength={100}
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2 text-foreground">Email</label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={handleChange}
            required
            maxLength={255}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-foreground">Business Type</label>
          <Select
            value={formData.businessType}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, businessType: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select your business type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dentist">Dentist / Clinic</SelectItem>
              <SelectItem value="cosmetic">Cosmetic Clinic</SelectItem>
              <SelectItem value="immigration">Immigration Consultant</SelectItem>
              <SelectItem value="education">Education / Training</SelectItem>
              <SelectItem value="service">Other Service Business</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-foreground">Monthly Ad Spend</label>
          <Select
            value={formData.adSpend}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, adSpend: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select your ad spend range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="under-1k">Less than $1,000</SelectItem>
              <SelectItem value="1k-5k">$1,000 – $5,000</SelectItem>
              <SelectItem value="5k-plus">$5,000+</SelectItem>
              <SelectItem value="not-running">Not running ads yet</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label htmlFor="message" className="block text-sm font-medium mb-2 text-foreground">What's your biggest challenge?</label>
          <Textarea
            id="message"
            name="message"
            placeholder="e.g., Not enough bookings, leads going cold, no follow-up system..."
            value={formData.message}
            onChange={handleChange}
            required
            maxLength={1000}
            rows={3}
          />
        </div>
        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Get My Free Audit"}
        </Button>
      </form>
    </motion.div>
  );
};

export default ContactForm;
